<template>
  <v-expansion-panels multiple :model-value="expansionValues">
    <slot name="prepended-panel"></slot>
    <v-expansion-panel
      :class="{ disabled: readOnly }"
      v-for="(group, settingGroup) in settings"
      :key="settingGroup"
      :data-testid="`settings-panel-${camelCase(settingGroup)}`">
      <v-expansion-panel-title
        ripple
        v-if="!settingGroupNamesToNotDynamicallyRender.includes(settingGroup)"
        :data-testid="`settings-panel-title-${camelCase(settingGroup)}`">
        {{ t(`core.settings.groups.${camelCase(settingGroup)}`) }}
        <v-chip
          class="text-uppercase ml-2"
          size="small"
          density="compact"
          variant="tonal"
          v-if="settingGroup === SettingGroups.GATE_MANAGEMENT">
          <v-icon
            icon="mdi-circle"
            size="x-small"
            :color="isGateManagementEnabled ? 'success' : 'error'"
            class="mr-2" />
          <strong>
            {{
              isGateManagementEnabled
                ? t('warehouse.pages.settings.expansionPanels.gateManagement.featureEnabled')
                : t('warehouse.pages.settings.expansionPanels.gateManagement.featureDisabled')
            }}
          </strong>
        </v-chip>
      </v-expansion-panel-title>
      <v-divider></v-divider>
      <v-expansion-panel-text
        v-if="!settingGroupNamesToNotDynamicallyRender.includes(settingGroup)">
        <gate-management-checklist
          :warehouse="entityParentObj"
          :is-gate-management-enabled="isGateManagementEnabled"
          v-if="settingGroup === SettingGroups.GATE_MANAGEMENT" />

        <gate-management-reference-settings
          v-if="settingGroup === SettingGroups.GATE_MANAGEMENT"
          :is-gate-management-enabled="isGateManagementEnabled"
          :entity-settings="localSettings"
          :read-only="readOnly" />

        <gate-management-check-in-sms-settings
          v-if="settingGroup === SettingGroups.GATE_MANAGEMENT"
          :is-gate-management-enabled="isGateManagementEnabled"
          :entity-settings="localSettings"
          :warehouse="entityParentObj"
          :read-only="readOnly" />

        <unit-limit-settings-group
          v-if="settingGroup === SettingGroups.LIMITER"
          :warehouse="entityParentObj"
          :entity-settings="localSettings" />

        <handling-units-settings-group
          v-if="settingGroup === SettingGroups.HANDLING_UNITS"
          :entity-settings="localSettings"
          :entity-parent-obj="entityParentObj"
          :read-only="readOnly"
          @settingsUpdated="setSettings" />

        <div
          class="setting-panel-content"
          v-for="(setting, key, idx) in group"
          :key="setting.label">
          <div
            class="font-size-small mt-4 mb-6"
            v-if="
              !idx &&
              getSettingGroupDescriptionMeta(setting)?.description &&
              settingGroup !== SettingGroups.HANDLING_UNITS
            ">
            <span class="d-inline-block mr-1">
              {{ getSettingGroupDescriptionMeta(setting)?.description }}
            </span>
            <a
              target="_blank"
              rel="noopener"
              class="font-size-x-small"
              v-if="getSettingGroupDescriptionMeta(setting)?.link"
              :href="getSettingGroupDescriptionMeta(setting)?.link">
              {{
                getSettingGroupDescriptionMeta(setting)?.linkText ??
                t('warehouse.pages.settings.expansionPanels.links.learnMore')
              }}
            </a>
          </div>
          <div
            v-if="setting.sectionLabel && !settingKeysToNotDynamicallyRender.includes(key)"
            class="mt-6 mb-2">
            <span class="typography-body-sm-bold text-medium-emphasis">
              {{ setting.sectionLabel }}
            </span>
          </div>

          <v-tooltip
            location="top"
            v-if="areRequiredFieldsSet(entityParentObj, setting)"
            :disabled="areSettingRequirementsMet(key, setting, localSettings) && !readOnly"
            :color="!readOnly ? 'error' : 'default'">
            <template
              #activator="{ props }"
              v-if="!settingKeysToNotDynamicallyRender.includes(key)">
              <div v-bind="props" :class="{ 'mt-6': idx }">
                <template v-if="setting.isActive">
                  <div
                    class="setting-input is-relative"
                    :class="{
                      'my-4': !idx && !getSettingGroupDescriptionMeta(setting)?.description
                    }">
                    <label
                      :for="getInputId(setting)"
                      class="font-size-small d-flex v-label"
                      v-if="!isSettingHidden(key, setting, localSettings)">
                      <strong class="d-block mr-2">{{ setting.label }}</strong>
                      <help-icon-tooltip v-if="setting.description" icon-style-class="grey">
                        {{ setting.description }}
                      </help-icon-tooltip>
                      <help-icon-tooltip
                        v-if="isInherited(key) && isInheritable(key)"
                        icon-name="mdi-alert-circle">
                        {{ getInheritedSettingMessage(key) }}
                      </help-icon-tooltip>
                    </label>
                    <v-text-field
                      :data-testid="getInputId(setting)"
                      class="mt-2 w-95"
                      v-if="
                        setting.inputType === SettingInputType.String &&
                        !isSettingHidden(key, setting, localSettings)
                      "
                      v-model="localSettings[key]"
                      :disabled="isSettingDisabled(key, setting)"
                      :placeholder="setting.placeholder ?? setting.defaultValue"
                      :hint="setting.note"
                      variant="outlined"
                      clearable
                      :hide-details="!setting.note"
                      persistent-hint
                      persistent-placeholder>
                      <template #message="{ message, key }">
                        <span v-html="message"></span>
                      </template>
                    </v-text-field>

                    <v-textarea
                      :data-testid="getInputId(setting)"
                      :id="getInputId(setting)"
                      class="mt-2 w-95"
                      v-if="
                        setting.inputType === SettingInputType.LongString &&
                        !isSettingHidden(key, setting, localSettings)
                      "
                      v-model="localSettings[key]"
                      :disabled="isSettingDisabled(key, setting)"
                      :placeholder="setting.placeholder ?? setting.defaultValue"
                      :hint="setting.note"
                      variant="outlined"
                      clearable
                      :hide-details="!setting.note"
                      persistent-hint
                      persistent-placeholder>
                      <template #message="{ message, key }">
                        <span v-html="message"></span>
                      </template>
                    </v-textarea>

                    <v-select
                      :data-testid="getInputId(setting)"
                      :id="getInputId(setting)"
                      class="mt-2 w-95"
                      v-if="
                        setting.inputType === SettingInputType.Bool &&
                        !isSettingHidden(key, setting, localSettings)
                      "
                      :disabled="isSettingDisabled(key, setting)"
                      variant="outlined"
                      :hide-details="!setting.note"
                      persistent-hint
                      v-model="localSettings[key]"
                      :items="getBoolOptions(key, setting)"
                      item-title="label"
                      item-value="value"
                      :hint="setting.note">
                      <template #message="{ message, key }">
                        <span v-html="message"></span>
                      </template>
                      <template #selection="{ item, index }">
                        <span v-html="item.raw.label"></span>
                      </template>
                      <template #item="{ item, props }">
                        <v-list-item v-bind="props" :title="null">
                          <v-list-item-title
                            v-html="item.raw.label"
                            :class="{
                              'font-weight-medium primary--text':
                                item.raw.value === localSettings[key]
                            }"></v-list-item-title>
                          <v-list-item-subtitle
                            v-if="item.raw.example"
                            class="text--black font-size-x-small">
                            {{ item.raw.example }}
                          </v-list-item-subtitle>
                        </v-list-item>
                      </template>
                    </v-select>

                    <v-select
                      :data-testid="getInputId(setting)"
                      :id="getInputId(setting)"
                      class="mt-2 w-95"
                      v-if="
                        setting.inputType === SettingInputType.DropDown &&
                        !isSettingHidden(key, setting, localSettings)
                      "
                      :disabled="isSettingDisabled(key, setting)"
                      variant="outlined"
                      :hide-details="!setting.note"
                      persistent-hint
                      v-model="localSettings[key]"
                      :items="getDropDownOptions(key, setting.dropDownOptions)"
                      item-title="label"
                      item-value="value"
                      :hint="setting.note">
                      <template #message="{ message, key }">
                        <span v-html="message"></span>
                      </template>
                      <template #selection="{ item, index }">
                        <span v-html="item.raw.label"></span>
                      </template>
                      <template #item="{ item, props }">
                        <v-list-item v-bind="props" :title="null">
                          <v-list-item-title
                            :title="null"
                            v-html="item.raw.label"
                            :class="{
                              'font-weight-medium primary--text':
                                item.raw.value === localSettings[key]
                            }"></v-list-item-title>
                        </v-list-item>
                      </template>
                    </v-select>

                    <clock-selector
                      class="w-95"
                      :id="getInputId(setting)"
                      :disabled="isSettingDisabled(key, setting)"
                      outlined
                      :hide-details="!setting.note"
                      persistent-hint
                      :description="setting.note"
                      v-model="localSettings[key]"
                      v-if="
                        setting.inputType === SettingInputType.ClockArray &&
                        !isSettingHidden(key, setting, localSettings)
                      "></clock-selector>
                    <template v-if="setting.inputType === SettingInputType.Document">
                      <div class="d-flex align-center mt-2">
                        <document-upload-button
                          :data-testid="getInputId(setting)"
                          :read-only="readOnly"
                          :crop-shape="setting.documentOptions?.cropShape"
                          :aspectRatio="setting.documentOptions?.aspectRatio"
                          :show-crop-result="setting.documentOptions?.showCropResult"
                          :modal-header-text="setting.documentOptions?.modalHeaderText"
                          allow-cropping
                          :max-size="setting.maxFileSize ?? 1024"
                          :conversion-unit="setting.fileSizeUnit ?? FileUnits.MB"
                          :allowed-mime-types="setting.allowedMimeTypes"
                          class="flex-grow-0"
                          v-model="localSettings[key]"></document-upload-button>
                      </div>
                      <div class="v-text-field__details">
                        <div class="v-messages theme--light">
                          <div class="v-messages__wrapper">
                            <div class="v-messages__message">{{ setting.description }}</div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <template
                      v-if="
                        setting.inputType === SettingInputType.Number &&
                        !isSettingHidden(key, setting, localSettings)
                      ">
                      <v-text-field
                        :data-testid="getInputId(setting)"
                        type="number"
                        :id="getInputId(setting)"
                        class="mt-2 w-95"
                        :model-value="localSettings[key]"
                        :rules="[getNumberMinRule(setting, key), getNumberMaxRule(setting, key)]"
                        @update:model-value="val => onNumberSettingChange(key, val)"
                        :disabled="isSettingDisabled(key, setting)"
                        :placeholder="setting.placeholder || String(setting.defaultValue)"
                        :hint="getNumberFieldHint(setting)"
                        variant="outlined"
                        clearable
                        persistent-hint
                        persistent-placeholder></v-text-field>
                    </template>

                    <v-text-field
                      :data-testid="getInputId(setting)"
                      :id="getInputId(setting)"
                      class="mt-2 w-95"
                      :rules="[...$validator.rules.email]"
                      v-if="
                        setting.inputType === SettingInputType.Email &&
                        !isSettingHidden(key, setting, localSettings)
                      "
                      v-model="localSettings[key]"
                      :disabled="isSettingDisabled(key, setting)"
                      :placeholder="setting.placeholder ?? setting.defaultValue"
                      :hint="setting.note"
                      variant="outlined"
                      clearable
                      :hide-details="!setting.note"
                      persistent-hint
                      persistent-placeholder
                      type="email">
                      <template #message="{ message, key }">
                        <span v-html="message"></span>
                      </template>
                    </v-text-field>

                    <!-- ERROR HERE? -->
                    <template
                      v-if="
                        !isSettingHidden(key, setting, localSettings) &&
                        shouldDisplayError(key, setting, localSettings)
                      ">
                      <v-tooltip location="right" max-width="250">
                        <template #activator="{ props }">
                          <v-icon color="error" v-bind="props" class="setting-error font-size-base">
                            mdi-alert
                          </v-icon>
                        </template>
                        <span>{{ setting.errorMessage }}</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>
              </div>
            </template>
            <span v-html="makeFieldTooltipText(key, setting)"></span>
          </v-tooltip>
        </div>
        <email-notifications-list
          class="mt-8"
          :entity-settings="localSettings"
          @settingsUpdated="setSettings"
          v-if="
            isVisibleMutedAppointmentNotifications(localSettings) &&
            settingGroup === SettingGroups.APPOINTMENT_NOTIFICATIONS
          "></email-notifications-list>
        <grid-tile-config-editor
          :input-enabled="localSettings.useNewGridTiles"
          :input-config="localSettings.gridTileConfig"
          :warehouse="entityParentObj"
          @configUpdated="config => (localSettings.gridTileConfig = config)"
          @enabledUpdated="val => (localSettings.useNewGridTiles = val)"
          v-if="settingGroup === SettingGroups.CUSTOM_APPPOINTMENTS_GRID"></grid-tile-config-editor>
      </v-expansion-panel-text>
    </v-expansion-panel>
    <slot name="append-panel"></slot>
  </v-expansion-panels>
</template>

<script>
import {
  AppointmentStatus,
  areRequiredFieldsSet,
  areSettingRequirementsMet,
  DatabaseEntities,
  EntitySettings,
  FileUnits,
  getRoleActionError,
  getTranslatedSettingGroupMetadata,
  isFeatureEnabledForWarehouse,
  isSettingHidden,
  makeGroupedSettings,
  objExistsInArr,
  Settings,
  SettingGroups,
  SettingInputType,
  shouldDisplayError,
  sortObjectByKeys,
  t,
  toKebabCase,
  WarehouseFeatureEnum
} from '@nova/core';
import { cloneDeep, camelCase, has, isInteger, isNil } from 'lodash';
import UnitLimitSettingsGroup from '../../unit_limits/components/UnitLimitSettingsGroup.vue';
import HandlingUnitsSettingsGroup from './HandlingUnitsSettingsGroup.vue';
import GateManagementReferenceSettings from './GateManagementReferenceSettings.vue';
import GateManagementCheckInSmsSettings from './GateManagementCheckInSmsSettings.vue';
export default {
  name: 'SettingsExpansionPanels',
  components: {
    UnitLimitSettingsGroup,
    HandlingUnitsSettingsGroup,
    GateManagementReferenceSettings,
    GateManagementCheckInSmsSettings
  },
  props: {
    entityKey: {
      type: String,
      required: true
    },
    entitySettings: {
      type: Object,
      required: true
    },
    shouldBeExpanded: {
      type: Boolean,
      required: false,
      default: false
    },
    entityParent: {
      type: String,
      required: false,
      default: 'Default'
    },
    entityParentObj: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    settings() {
      const settings = makeGroupedSettings(
        this.isOrgSettings ? 'Organization' : this.entityKey,
        true
      );

      // Do not show the Gate Management group entries if the feature is not enabled
      if (!this.isGateManagementEnabled) {
        settings[SettingGroups.GATE_MANAGEMENT] = [];
      }

      // Hide ID validation if feature is disabled
      if (!this.isIdValidationEnabled) {
        delete settings[SettingGroups.ID_VALIDATION];
      }

      if (!this.isSmartGateEnabled) {
        delete settings[SettingGroups.SMARTGATE];
      }

      if (this.entityKey.toLowerCase() !== 'warehouse') {
        delete settings[SettingGroups.GATE_MANAGEMENT];
      }

      // Surface "Include Reference Number field on all appointment forms?" as the first
      // field of the group so it reads as the entry point for the reference number
      // settings (min/max and the rest follow it). See OD-2692.
      const refGroup = settings[SettingGroups.REFERENCE_NUMBER];
      if (refGroup?.referenceNumberIsVisible) {
        const { referenceNumberIsVisible, ...rest } = refGroup;
        settings[SettingGroups.REFERENCE_NUMBER] = { referenceNumberIsVisible, ...rest };
      }

      return sortObjectByKeys(settings);
    },
    isOrgSettings() {
      return this.entityKey === DatabaseEntities.Org;
    },
    isGateManagementEnabled() {
      return this.entityKey.toLowerCase() === 'warehouse'
        ? isFeatureEnabledForWarehouse(this.entityParentObj, WarehouseFeatureEnum.GateManagement)
        : false;
    },
    isSmartGateEnabled() {
      return this.entityKey.toLowerCase() === 'warehouse'
        ? isFeatureEnabledForWarehouse(this.entityParentObj, WarehouseFeatureEnum.SmartGate)
        : false;
    },
    isIdValidationEnabled() {
      return this.entityKey.toLowerCase() === 'warehouse'
        ? isFeatureEnabledForWarehouse(this.entityParentObj, WarehouseFeatureEnum.IdValidation)
        : false;
    }
  },
  data() {
    return {
      areRequiredFieldsSet,
      areSettingRequirementsMet,
      FileUnits,
      isSettingHidden,
      SettingGroups,
      SettingInputType,
      shouldDisplayError,
      expansionValues: [],
      localSettings: {},
      settingKeysToNotDynamicallyRender: [
        'mutedAppointmentNotifications',
        'useNewGridTiles',
        'gridTileConfig',
        'includeHandlingUnitsFieldOnAppointmentForms',
        'handlingUnitsLabel',
        'handlingUnitsHelperText',
        'isHandlingUnitsRequired',
        'handlingUnitsMinimumThreshold',
        'handlingUnitsMaximumThreshold',
        'enableOverrideLimiterFunction',
        'gateManagementDriverReferenceRequired',
        'gateManagementDriverReferenceValidated',
        'gateManagementCustomFirstSmsMessage',
        'gateManagementIncludeYardMapInFirstSms'
      ],
      settingGroupNamesToNotDynamicallyRender: ['Appointment Validation']
    };
  },
  methods: {
    t,
    camelCase,
    isInteger,
    makeFieldTooltipText(settingKey, setting) {
      if (this.readOnly) {
        return getRoleActionError();
      }
      const reqsMet = this.areSettingRequirementsMet(settingKey, setting, this.localSettings);

      if (reqsMet) {
        return setting.description;
      }

      const lines = [
        this.t('warehouse.pages.settings.expansionPanels.tooltip.fieldRequirementsHeading')
      ];

      Object.keys(setting.requirements).forEach(reqKey => {
        const v = setting.requirements[reqKey];
        const reqV =
          v === true
            ? this.t('warehouse.pages.settings.expansionPanels.states.enabled')
            : v === false
            ? this.t('warehouse.pages.settings.expansionPanels.states.disabled')
            : this.t('warehouse.pages.settings.expansionPanels.tooltip.requirementValueQuoted', {
                value: v
              });
        lines.push(
          this.t('warehouse.pages.settings.expansionPanels.tooltip.requirementLine', {
            settingLabel: Settings[reqKey].label,
            requirement: reqV
          })
        );
      });

      return lines.join('<br />');
    },
    isInherited(settingKey) {
      return this.isOrgSettings ? false : !has(this.localSettings, settingKey);
    },
    isInheritable(settingKey) {
      return this.isOrgSettings ? false : has(EntitySettings[this.entityParent], settingKey);
    },
    isSettingDisabled(key, setting) {
      return !areSettingRequirementsMet(key, setting, this.localSettings) || this.readOnly;
    },
    getInheritedSettingMessage(settingKey) {
      if (!this.isInheritable(settingKey)) {
        return '';
      }
      return this.t(
        'warehouse.pages.settings.messages.ifNoOptionIsSelectedSettingWillBeInheritedFromParent',
        { entityParent: this.entityParent }
      );
    },
    getDefaultValue(settingKey) {
      // GROSS!  Special case "muteAppointmentNotifications" here because the UI reverses the meaning - disabled => enable
      const defaultVal = EntitySettings[this.entityKey][settingKey].defaultValue;
      const valsToFlip = ['muteAppointmentNotifications'];
      return valsToFlip.includes(settingKey) ? !defaultVal : defaultVal;
    },
    setDefaultFlagOnOptions(options, settingKey) {
      options = options.map(option => {
        if (this.isInheritable(settingKey)) {
          return option;
        } else {
          if (option.value === this.getDefaultValue(settingKey)) {
            const defaultMarker = this.t(
              'warehouse.pages.settings.expansionPanels.dropdown.defaultMarker'
            );
            option.label += ` <span class="d-inline-block ml-2 font-size-x-small grey--text text--darken-1">${defaultMarker}</span>`;
          }
          return option;
        }
      });
      return options;
    },
    getBoolOptions(settingKey, setting) {
      let options = [
        {
          label:
            setting.booleanDropDownOptions?.whenTrue ||
            this.t('warehouse.pages.settings.expansionPanels.dropdown.yes'),
          example: setting.booleanDropDownOptions?.exampleTrue ?? '',
          value: true
        },
        {
          label:
            setting.booleanDropDownOptions?.whenFalse ||
            this.t('warehouse.pages.settings.expansionPanels.dropdown.no'),
          example: setting.booleanDropDownOptions?.exampleFalse ?? '',
          value: false
        }
      ];

      options = this.setDefaultFlagOnOptions(cloneDeep(options), settingKey);

      if (!this.isInheritable(settingKey)) {
        return options;
      } else {
        return has(this.localSettings, settingKey)
          ? this.appendOption(options, this.getUnsetOption(settingKey))
          : options;
      }
    },
    getDropDownOptions(settingKey, options) {
      if (this.isInheritable(settingKey)) {
        options = has(this.localSettings, settingKey)
          ? this.appendOption(options, this.getUnsetOption(settingKey))
          : this.removeOption(options, this.getUnsetOption(settingKey));
      }

      options = this.setDefaultFlagOnOptions(cloneDeep(options), settingKey);

      return options;
    },
    appendOption(options, newOption) {
      if (!objExistsInArr(options, newOption)) {
        options.push(newOption);
      }

      return options;
    },
    removeOption(options, newOption) {
      if (objExistsInArr(options, newOption)) {
        const index = options.findIndex(item => JSON.stringify(item) === JSON.stringify(newOption));
        options.splice(index, 1);
      }

      return options;
    },
    isVisibleMutedAppointmentNotifications(settings) {
      // Note that this is inverted
      return settings?.muteAppointmentNotifications === true;
    },
    setSettings(settings) {
      Object.keys(settings).forEach(key => {
        if (settings[key] === null) {
          delete settings[key];
        }

        if (!this.isVisibleMutedAppointmentNotifications(settings)) {
          delete settings.mutedAppointmentNotifications;
        }
      });

      // Clean up handling units settings if the main toggle is off
      if (settings.includeHandlingUnitsFieldOnAppointmentForms !== true) {
        delete settings.handlingUnitsLabel;
        delete settings.handlingUnitsHelperText;
        delete settings.isHandlingUnitsRequired;
        delete settings.handlingUnitsMinimumThreshold;
        delete settings.handlingUnitsMaximumThreshold;
      }

      if (settings.appointmentCreationStatus !== AppointmentStatus.Requested) {
        delete settings.revertToRequestedOnCarrierReschedule;
      }

      const settingsMetaData = EntitySettings[this.entityKey] ?? {};
      const hasInvalidValue = Object.keys(settings).some(key => {
        const meta = settingsMetaData[key];
        if (!meta) return false;
        const hasNumericBounds = isInteger(meta.maxValue) || isInteger(meta.minValue);
        if (hasNumericBounds) {
          if (isNil(settings[key])) {
            return !this.isInheritable(key);
          }
          const numVal = Number(settings[key]);
          if (!Number.isFinite(numVal)) return true;
          if (isInteger(meta.maxValue) && numVal > meta.maxValue) return true;
          if (isInteger(meta.minValue) && numVal < meta.minValue) return true;
        }
        return false;
      });
      this.$emit('settings-valid', !hasInvalidValue);
      if (hasInvalidValue) return;

      this.$emit('update-settings', cloneDeep(settings));
    },
    onNumberSettingChange(key, value) {
      this.localSettings[key] = value === '' || value === null ? null : Number(value);
    },
    getSettingGroupDescriptionMeta(setting) {
      return getTranslatedSettingGroupMetadata(this.entityKey)[setting.group];
    },
    getInputId(setting) {
      return toKebabCase(setting.label).toLowerCase();
    },
    isDefaultValue(setting, value) {
      return setting.defaultValue === value;
    },
    getUnsetOption(settingKey) {
      if (this.isInheritable(settingKey)) {
        return {
          label: this.t('warehouse.pages.settings.dropdownOptions.common.unsetToParentSettings', {
            entityParent: this.entityParent
          }),
          value: null
        };
      }
    },
    getNumberMinRule(setting, key) {
      return value => {
        if (!isInteger(setting.minValue)) {
          return true;
        }
        if (isNil(value)) {
          return (
            this.isInheritable(key) ||
            this.t('warehouse.pages.settings.expansionPanels.validation.valueMustBeAtLeast', {
              minValue: setting.minValue
            })
          );
        }
        return (
          +value >= +setting.minValue ||
          this.t('warehouse.pages.settings.expansionPanels.validation.valueMustBeAtLeast', {
            minValue: setting.minValue
          })
        );
      };
    },
    getNumberMaxRule(setting, key) {
      return value => {
        if (!isInteger(setting.maxValue)) {
          return true;
        }
        if (isNil(value)) {
          return (
            this.isInheritable(key) ||
            this.t('warehouse.pages.settings.expansionPanels.validation.valueMustBeAtMost', {
              maxValue: setting.maxValue
            })
          );
        }
        return (
          +value <= +setting.maxValue ||
          this.t('warehouse.pages.settings.expansionPanels.validation.valueMustBeAtMost', {
            maxValue: setting.maxValue
          })
        );
      };
    },
    getNumberFieldHint(setting) {
      if (!isInteger(setting.maxValue)) {
        return setting.note ?? '';
      }
      const maxPart = this.t('warehouse.pages.settings.expansionPanels.validation.maximumValue', {
        maxValue: setting.maxValue
      });
      return setting.note ? `${maxPart}. ${setting.note}` : maxPart;
    }
  },
  mounted() {
    this.localSettings = cloneDeep(this.entitySettings);
    Object.keys(EntitySettings[this.entityKey]).map(key => {
      if (!this.isInheritable(key) && this.localSettings[key] == undefined) {
        this.localSettings[key] = EntitySettings[this.entityKey][key].defaultValue;
      }
    });
    if (this.shouldBeExpanded) {
      this.expansionValues = Array.from({ length: Object.keys(this.settings).length }, (v, i) => i);
    }
  },
  watch: {
    localSettings: {
      handler(settings) {
        this.setSettings(settings);
      },
      deep: true
    },
    // When the entity unsets "Make Reference Number a required field?" to inherit from the
    // parent, cascade the same "unset" to the min/max and input mode fields so the whole
    // reference number configuration falls back to the parent's settings. See OD-2692.
    'localSettings.referenceNumberIsRequired'(value) {
      if (isNil(value)) {
        this.localSettings.referenceNumbersMinimumCount = null;
        this.localSettings.referenceNumbersMaximumCount = null;
        this.localSettings.referenceNumberInputMode = null;
      }
    }
  }
};
</script>
